import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  FaBalanceScale,
  FaCalendarAlt,
  FaFileExcel,
  FaEllipsisV,
  FaPlus,
  FaEye,
  FaTimes,
  FaReceipt,
  FaSearch,
  FaSpinner,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaWallet,
  FaCoins,
  FaTrash,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import apiRequest from "../services/api";
import PaymentReceipt from "../components/PaymentReceipt";
import { exportPaymentsToExcel } from "../utils/exportPaymentsToExcel";

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [actualAccounts] = useState([
    { id: "CASH", name: "CASH" },
    { id: "BANK", name: "BANK" },
  ]);
  const [journalAccounts] = useState([
    { id: "SUSPENSE", name: "Suspense" },
    { id: "CASH", name: "CASH" },
    { id: "BANK", name: "BANK" },
    { id: "ACCOUNTS_RECEIVABLE", name: "Accounts Receivable" },
    { id: "ACCOUNTS_PAYABLE", name: "Accounts Payable" },
    { id: "OWNER_CAPITAL", name: "Owner Capital" },
    { id: "OWNER_DRAWINGS", name: "Owner Drawings" },
    { id: "LOAN_PAYABLE", name: "Loan Payable" },
    { id: "OTHER_REVENUE", name: "Other Revenue" },
    { id: "OTHER_EXPENSE", name: "Other Expense" },
    { id: "WITHHOLDING_PAYABLE", name: "Withholding Payable" },
    { id: "PAYE_PAYABLE", name: "PAYE Payable" },
    { id: "PENSION_PAYABLE", name: "Pension Payable" },
    { id: "PROFIT_TAX_PAYABLE", name: "Profit Tax Payable" },
    { id: "REFUND_RECEIVABLE", name: "Refund Receivable" },
  ]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [directionFilter, setDirectionFilter] = useState("ALL");
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const PAGE_SIZE_OPTIONS = [15, 20, 50, 100, 1000, 2000, 5000, 10000];

  // Action Menu
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activePayment, setActivePayment] = useState(null);
  const [receiptPaymentId, setReceiptPaymentId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Receipt / Print
  const [openReceipt, setOpenReceipt] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");

  const today = new Date();
  const currentYear = today.getFullYear();

  const [filterStartDate, setFilterStartDate] = useState(
    `${currentYear}-01-01`
  );

  const [filterEndDate, setFilterEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const initialMiscRevenueForm = {
    amount: 0.0,
    methodStr: "",
    reference: "",
    description: "",
    typeStr: "",
    date: "",
  };

  const initialExpensePaymentForm = {
    expense: { id: "" },
    amount: 0.0,
    methodStr: "",
    description: "",
    date: "",
  };

  const initialInternalTransferForm = {
    fromAccount: "",
    toAccount: "",
    amount: 0.0,
    reference: "",
    description: "",
    date: "",
  };

  const [miscRevenueForm, setMiscRevenueForm] = useState(
    initialMiscRevenueForm
  );
  const [expensePaymentForm, setExpensePaymentForm] = useState(
    initialExpensePaymentForm
  );
  const [internalTransferForm, setInternalTransferForm] = useState(
    initialInternalTransferForm
  );

  const toLocalDateTime = (d) => {
    if (!d) return null;
    // "2026-09-24" → "2026-09-24T00:00:00"
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00`;
    return d; // already has time
  };

  const resetForms = () => {
    setMiscRevenueForm(initialMiscRevenueForm);
    setExpensePaymentForm(initialExpensePaymentForm);
    setInternalTransferForm(initialInternalTransferForm);
  };

  const [selectedPayment, setSelectedPayment] = useState(null);

  // =====================================================
  // CLOSE MENU HELPER
  // =====================================================
  const closeMenu = () => {
    setActiveMenuId(null);
    setMenuAnchor(null);
    setActivePayment(null);
    setConfirmDeleteId(null);
  };

  //////sorting
  const [sortConfig, setSortConfig] = useState({
    key: "paymentDate",
    direction: "desc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedPayments = useMemo(() => {
    if (!payments?.length) return [];

    return [...payments].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Dates
      if (sortConfig.key === "paymentDate") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Numbers (amount / settlement)
      if (sortConfig.key === "amount") {
        aValue = Number(a.amount || a.settlementAmount || 0);
        bValue = Number(b.amount || b.settlementAmount || 0);
      }

      // Strings
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [payments, sortConfig]);

  const filteredPayments = useMemo(() => {
    return sortedPayments.filter((p) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        (p.status || "").toUpperCase() === statusFilter;

      const matchesDirection =
        directionFilter === "ALL" ||
        (directionFilter === "IN" &&
          (p.direction === "IN" || p.type === "INTERNAL_TRANSFER")) ||
        (directionFilter === "OUT" &&
          p.direction === "OUT" &&
          p.type !== "INTERNAL_TRANSFER");

      const matchesMethod =
        methodFilter === "ALL" ||
        (p.method || "").toUpperCase() === methodFilter;

      const matchesType =
        typeFilter === "ALL" || (p.type || "").toUpperCase() === typeFilter;

      return matchesStatus && matchesDirection && matchesMethod && matchesType;
    });
  }, [sortedPayments, statusFilter, directionFilter, methodFilter, typeFilter]);

  const pageTotals = useMemo(() => {
    return filteredPayments.reduce(
      (acc, p) => {
        const status = (p.status || "").toUpperCase();
        if (status !== "APPROVED") return acc;

        const amount = Number(
          Number(p.amount || 0) > 0 ? p.amount : p.settlementAmount || 0
        );
        const commission = Number(p.bankCommission || 0);

        const isIn = p.direction === "IN" || p.type === "INTERNAL_TRANSFER";

        if (isIn) {
          acc.inAmount += amount;
          acc.inCount += 1;
        } else {
          acc.outAmount += amount;
          acc.outCount += 1;
        }

        acc.bankCommission += commission;
        acc.count += 1;
        return acc;
      },
      {
        inAmount: 0,
        outAmount: 0,
        bankCommission: 0,
        inCount: 0,
        outCount: 0,
        count: 0,
      }
    );
  }, [filteredPayments]);

  const formatMoney = (n) =>
    Number(n || 0).toLocaleString("en-ET", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-6 py-4 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white transition ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div
          className={`flex items-center gap-1.5 ${
            className.includes("text-right") ? "justify-end" : ""
          }`}
        >
          <span>{label}</span>
          <span className="text-[10px] opacity-70">
            {direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕"}
          </span>
        </div>
      </th>
    );
  };

  // Close menu on outside click / Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".action-menu") &&
        !e.target.closest(".ellipsis-trigger-btn")
      ) {
        closeMenu();
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    if (activeMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [activeMenuId]);

  // =====================================================
  // DATA CORE SYNC PIPELINE (paginated)
  // =====================================================
  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.append("keyword", keyword.trim());
      if (filterStartDate) {
        params.append("startDate", `${filterStartDate}T00:00:00`);
      }
      if (filterEndDate) {
        params.append("endDate", `${filterEndDate}T23:59:59`);
      }
      params.append("page", page.toString());
      params.append("size", size.toString());
      const resp = await apiRequest(`payments?${params.toString()}`);
      const content = (resp.data?.content || []).filter(Boolean);

      setPayments(content);
      setTotalPages(resp.data?.totalPages || 0);
      setTotalElements(resp.data?.totalElements || 0);
    } catch (err) {
      setError(err?.message || "Failed to synchronize payment records.");
      setPayments([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  }, [keyword, filterStartDate, filterEndDate, page, size]);

  // Reset to first page when keyword or size changes
  useEffect(() => {
    setPage(0);
  }, [keyword, filterStartDate, filterEndDate, size]);

  // Fetch whenever page / keyword / size changes
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    if (!isModalOpen) {
      resetForms();
      setSelectedPayment(null);
    }
  }, [isModalOpen]);

  // =====================================================
  // APPROVE ACTION LIFECYCLE
  // =====================================================
  const handleApprovePayment = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to approve this payment?\n\nThis cannot be undone."
    );

    if (!confirmed) return;

    setError("");
    try {
      await apiRequest(`payments/${id}`, {
        method: "PUT",
      });
      setPage(0);
      fetchPayments();
    } catch (err) {
      setError(err?.message || "Payment record approval failed.");
    }
  };

  // =====================================================
  // DELETION ACTION LIFECYCLE
  // =====================================================
  const handleDeletePayment = async (id) => {
    setError("");
    try {
      await apiRequest(`payments/${id}`, {
        method: "DELETE",
      });
      setPage(0);
      fetchPayments();
    } catch (err) {
      setError(
        err?.message || "Engine blocked payment ledger record deletion."
      );
    }
  };

  // =====================================================
  // MODAL TRIGGER HANDLERS
  // =====================================================
  const openActionModal = (mode, data = null) => {
    setModalMode(mode);
    setError("");

    if (mode === "view") {
      setSelectedPayment(data);
    } else if (mode === "misc-revenue") {
      setMiscRevenueForm({
        amount: 0.0,
        methodStr: "",
        reference: "",
        description: "",
        typeStr: "",
        date: "",
      });
    } else if (mode === "expense") {
      setExpensePaymentForm({
        expense: { id: "" },
        amount: 0.0,
        description: "",
        methodStr: "",
        date: "",
      });
    } else if (mode === "transfer") {
      setInternalTransferForm({
        fromAccount: "",
        toAccount: "",
        amount: 0.0,
        reference: "",
        description: "",
        date: "",
      });
    } else if (mode === "correction") {
      setInternalTransferForm({
        fromAccount: "",
        toAccount: "",
        amount: 0.0,
        reference: "",
        description: "",
        date: "",
      });
    }
    setIsModalOpen(true);
  };

  const isAutoSettlement = ["TAX_SETTLEMENT", "PROFIT_TAX_PAYMENT"].includes(
    miscRevenueForm.typeStr
  );

  const handleExport = () => {
    exportPaymentsToExcel(filteredPayments, "payments");
  };

  // =====================================================
  // TRANSACTION ROUTING SUBMISSIONS
  // =====================================================
  const handleSavePayment = async (e) => {
    e.preventDefault();
    setError("");

    // "2026-09-24" → "2026-09-24T00:00:00" (Jackson LocalDateTime)
    const toLocalDateTime = (d) => {
      if (!d) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00`;
      return d;
    };

    let endpoint = "payments/";
    let payload = {};

    if (modalMode === "misc-revenue") {
      if (!miscRevenueForm.typeStr)
        return setError("Payment type is required.");

      if (!isAutoSettlement && parseFloat(miscRevenueForm.amount) <= 0)
        return setError("Amount must be strictly greater than 0.00.");

      endpoint += "recordPayment";

      payload = {
        ...miscRevenueForm,
        amount: isAutoSettlement ? 0 : miscRevenueForm.amount,
        date: toLocalDateTime(miscRevenueForm.date),
      };
    } else if (modalMode === "expense") {
      if (parseFloat(expensePaymentForm.amount) <= 0)
        return setError("Amount must be strictly greater than 0.00.");
      endpoint += "expense";
      payload = {
        ...expensePaymentForm,
        date: toLocalDateTime(expensePaymentForm.date),
      };
    } else if (modalMode === "transfer") {
      if (parseFloat(internalTransferForm.amount) <= 0)
        return setError("Amount must be strictly greater than 0.00.");

      if (!internalTransferForm.fromAccount || !internalTransferForm.toAccount)
        return setError("Both 'From' and 'To' accounts must be selected.");
      if (internalTransferForm.fromAccount === internalTransferForm.toAccount)
        return setError("Source and Destination accounts cannot be identical.");

      endpoint += "internal-transfer";
      payload = {
        ...internalTransferForm,
        date: toLocalDateTime(internalTransferForm.date),
      };
    } else if (modalMode === "correction") {
      if (parseFloat(internalTransferForm.amount) <= 0) {
        return setError("Amount must be strictly greater than 0.00.");
      }

      if (
        !internalTransferForm.fromAccount ||
        !internalTransferForm.toAccount
      ) {
        return setError(
          "Both Debit (+) and Credit (−) accounts must be selected."
        );
      }

      if (internalTransferForm.fromAccount === internalTransferForm.toAccount) {
        return setError(
          "Debit (+) and Credit (−) accounts cannot be the same."
        );
      }

      // One of the accounts MUST be SUSPENSE
      const isFromSuspense = internalTransferForm.fromAccount === "SUSPENSE";
      const isToSuspense = internalTransferForm.toAccount === "SUSPENSE";

      if (!isFromSuspense && !isToSuspense) {
        return setError("One of the accounts must be SUSPENSE.");
      }

      if (isFromSuspense && isToSuspense) {
        return setError("Both accounts cannot be SUSPENSE.");
      }

      endpoint += "correction-journal";

      // If backend expects fromAccount/toAccount → keep spread
      payload = {
        ...internalTransferForm,
        date: toLocalDateTime(internalTransferForm.date),
      };
    }

    try {
      await apiRequest(endpoint, {
        method: "POST",
        body: payload,
      });
      setPage(0);
      fetchPayments();
      setIsModalOpen(false);
    } catch (err) {
      setError(
        err?.message || "Execution engine blocked transaction submission."
      );
    }
  };

  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800
      dark:bg-slate-950 dark:text-slate-100"
    >
      <div className="max-w-7xl mx-auto space-y-6 pb-24">
        {/* Head Panel */}
        <div
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          <div className="flex items-center gap-3.5">
            <div
              className="p-3 rounded-xl shadow-inner border
              bg-gradient-to-br from-emerald-100 to-teal-50 border-emerald-200 text-emerald-600
              dark:from-emerald-500/20 dark:to-teal-500/10 dark:border-emerald-500/30 dark:text-emerald-400"
            >
              <FaWallet className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Payment Ledger
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                Cash flows and payment management
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openActionModal("misc-revenue")}
              className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
            >
              <FaPlus /> Journal Entry
            </button>
            <button
              type="button"
              onClick={() => openActionModal("transfer")}
              className="flex items-center gap-2 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-blue-600/20"
            >
              <FaExchangeAlt /> Cash Transfer
            </button>
            <button
              type="button"
              onClick={() => openActionModal("correction")}
              className="flex items-center gap-2 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-blue-600/20"
            >
              <FaExchangeAlt /> Correction
            </button>

            <button
              type="button"
              onClick={() => handleExport()}
              disabled={!payments.length}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition border
                bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300
                dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-700/60
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <>
                <FaFileExcel className="text-sm" />
                Export
              </>
            </button>
          </div>
        </div>

        {/* Query and Date Filters - Single Row */}
        <div className="flex flex-nowrap items-center gap-3 w-full">
          {/* Query Bar */}
          <div className="relative flex-1 min-w-0">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400 dark:text-slate-500 text-xs">
              <FaSearch />
            </span>
            <input
              type="text"
              placeholder="Query by reference, direction, method..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full rounded-xl pl-9 pr-3 py-2.5 text-xs transition focus:outline-none focus:border-emerald-500/50
        bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
        dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>

          {/* Start Date */}
          <div className="relative w-40 shrink-0">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaCalendarAlt className="text-[10px]" />
            </span>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full rounded-xl pl-8 pr-2 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
        bg-slate-50 border border-slate-200 text-slate-800
        dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
              title="Invoice date from"
            />
          </div>

          {/* End Date */}
          <div className="relative w-40 shrink-0">
            <input
              type="date"
              value={filterEndDate}
              min={filterStartDate || undefined}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full rounded-xl px-3 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
        bg-slate-50 border border-slate-200 text-slate-800
        dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
              title="Invoice date to"
            />
          </div>
        </div>

        {/* Payment Filters */}
        <div
          className="flex flex-wrap items-center gap-3 p-3 rounded-xl border
  bg-white border-slate-200
  dark:bg-slate-900/20 dark:border-white/10"
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg px-3 py-2 text-xs border
      bg-slate-50 border-slate-200 text-slate-800
      dark:bg-slate-950 dark:border-white/10 dark:text-white"
          >
            <option value="ALL">All Statuses</option>
            <option value="APPROVED">Approved</option>
            <option value="DRAFT">Draft</option>
          </select>

          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className="rounded-lg px-3 py-2 text-xs border
      bg-slate-50 border-slate-200 text-slate-800
      dark:bg-slate-950 dark:border-white/10 dark:text-white"
          >
            <option value="ALL">All Directions</option>
            <option value="IN">Cash In</option>
            <option value="OUT">Cash Out</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="rounded-lg px-3 py-2 text-xs border
      bg-slate-50 border-slate-200 text-slate-800
      dark:bg-slate-950 dark:border-white/10 dark:text-white"
          >
            <option value="ALL">All Methods</option>
            <option value="CASH">Cash</option>
            <option value="BANK">Bank</option>
            <option value="MOBILE">Mobile</option>
            <option value="CHEQUE">Cheque</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg px-3 py-2 text-xs border
      bg-slate-50 border-slate-200 text-slate-800
      dark:bg-slate-950 dark:border-white/10 dark:text-white"
          >
            <option value="ALL">All Classifications</option>
            {[...new Set(payments.map((p) => p.type).filter(Boolean))]
              .sort()
              .map((type) => (
                <option key={type} value={type.toUpperCase()}>
                  {type.replace(/_/g, " ")}
                </option>
              ))}
          </select>

          <button
            type="button"
            onClick={() => {
              setStatusFilter("ALL");
              setDirectionFilter("ALL");
              setMethodFilter("ALL");
              setTypeFilter("ALL");
            }}
            className="px-3 py-2 rounded-lg text-xs font-semibold border
      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
      dark:bg-slate-800 dark:border-white/10 dark:text-slate-300"
          >
            Clear Filters
          </button>

          <span className="text-xs text-slate-500 dark:text-slate-400">
            {filteredPayments.length} matching records on this page
          </span>
        </div>

        {error && (
          <div
            className="p-4 rounded-xl text-xs font-medium border
            bg-red-50 border-red-200 text-red-700
            dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400"
          >
            {error}
          </div>
        )}

        {/* Main Ledger Grid Table */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md overflow-hidden
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
    bg-slate-50 border-slate-200 text-slate-500
    dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <th className="px-4 py-4 text-center">SN</th>
                  <SortableHeader label="Reference" sortKey="receiptNumber" />
                  <SortableHeader label="Customer" sortKey="customer" />
                  <SortableHeader label="Flow Direction" sortKey="direction" />
                  <SortableHeader label="Payment Method" sortKey="method" />
                  <SortableHeader label="Status" sortKey="status" />
                  <SortableHeader label="Classification" sortKey="type" />
                  <SortableHeader label="Payment Date" sortKey="paymentDate" />
                  <SortableHeader
                    label="Amount / Settlement"
                    sortKey="amount"
                    className="text-right"
                  />
                  <SortableHeader
                    label="Bank Comm."
                    sortKey="bankCommission"
                    className="text-right"
                  />

                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-emerald-500 text-base" />
                        <span>Loading Logs...</span>
                      </div>
                    </td>
                  </tr>
                ) : payments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No payment records found.
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((p, index) => (
                    <tr
                      key={p.id}
                      className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-4 py-4 text-center font-mono text-slate-500">
                        {page * size + index + 1}
                      </td>
                      <td className="px-6 py-4 font-mono font-semibold tracking-wide">
                        <div className="text-slate-800 dark:text-slate-200 font-medium max-w-xs truncate">
                          {p.receiptNumber || p.reference || "SYSTEM-GEN"}
                        </div>
                        {p.rrn && (
                          <div className="text-[10px] font-normal text-slate-500 dark:text-slate-500 flex items-start gap-1 mt-0.5 min-w-0">
                            <FaReceipt className="text-[9px] flex-shrink-0 mt-0.5" />
                            <span className="min-w-0 break-all">{p.rrn}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 font-mono text-[11px] font-medium text-slate-700 dark:text-slate-200">
                        {p.customer}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide inline-flex items-center gap-1 border ${
                            p.direction === "IN" ||
                            p.type === "INTERNAL_TRANSFER"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                              : "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/10"
                          }`}
                        >
                          {p.direction === "IN" ||
                          p.type === "INTERNAL_TRANSFER" ? (
                            <FaArrowDown className="text-[9px]" />
                          ) : (
                            <FaArrowUp className="text-[9px]" />
                          )}
                          {p.direction}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-mono text-[11px] font-medium text-slate-700 dark:text-slate-200">
                        {p.method}
                      </td>
                      <td
                        className={`px-6 py-4 font-mono text-[11px] font-medium ${
                          p.status === "DRAFT"
                            ? "text-orange-600 dark:text-orange-400 font-semibold"
                            : p.status === "CANCELLED"
                            ? "text-red-600 dark:text-red-400 font-semibold"
                            : "text-slate-700 dark:text-slate-200"
                        }`}
                      >
                        {p.status}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-[10px] border border-slate-200 font-medium
                          dark:text-slate-400 dark:bg-slate-800 dark:border-white/5"
                        >
                          {p.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                        {p.paymentDate
                          ? new Date(p.paymentDate).toLocaleString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "-"}
                      </td>
                      <td
                        className={`px-6 py-4 text-right font-mono font-bold text-sm ${
                          p.direction === "IN" || p.type === "INTERNAL_TRANSFER"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        {p.direction === "IN" || p.type === "INTERNAL_TRANSFER"
                          ? "+"
                          : "-"}
                        {Number(
                          Number(p.amount || 0) > 0
                            ? p.amount
                            : p.settlementAmount || 0
                        ).toLocaleString("en-ET", {
                          style: "currency",
                          currency: "ETB",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>

                      <td>
                        {Number(p.bankCommission ?? 0).toLocaleString("en-ET", {
                          style: "currency",
                          currency: "ETB",
                          minimumFractionDigits: 2,
                        })}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5 text-center font-sans">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (activeMenuId === p.id) {
                              closeMenu();
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({
                                top: rect.bottom + window.scrollY + 6,
                                left: rect.left + window.scrollX - 168,
                              });
                              setActivePayment(p);
                              setActiveMenuId(p.id);
                              setConfirmDeleteId(null);
                            }
                          }}
                          className="ellipsis-trigger-btn p-2 rounded-xl border transition
                            bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800
                            dark:bg-white/5 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
                        >
                          <FaEllipsisV className="text-xs pointer-events-none" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* ========== PAGE SUMMARY ========== */}
            {!isLoading && payments.length > 0 && (
              <div className="border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-950/40">
                <div className="px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Page summary
                  <span className="ml-2 font-mono font-normal normal-case text-slate-400">
                    ({pageTotals.count} records · only approved)
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-5 pb-4 text-xs">
                  {/* Cash In */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Cash In ({pageTotals.inCount})
                    </div>
                    <div className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatMoney(pageTotals.inAmount)}
                    </div>
                  </div>

                  {/* Cash Out */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Cash Out ({pageTotals.outCount})
                    </div>
                    <div className="font-mono font-semibold text-rose-600 dark:text-rose-400">
                      {formatMoney(pageTotals.outAmount)}
                    </div>
                  </div>

                  {/* Net Flow */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Net Flow
                    </div>
                    <div
                      className={`font-mono font-bold ${
                        pageTotals.inAmount - pageTotals.outAmount >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {formatMoney(pageTotals.inAmount - pageTotals.outAmount)}
                    </div>
                  </div>

                  {/* Bank Commission */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Bank Commission
                    </div>
                    <div className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                      {formatMoney(pageTotals.bankCommission)}
                    </div>
                  </div>

                  {/* Total Records */}
                  <div>
                    <div className="text-slate-500 dark:text-slate-400">
                      Records
                    </div>
                    <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                      {pageTotals.count}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pagination + page-size selector */}
          {(totalPages > 1 || totalElements > 0) && (
            <div
              className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs
              bg-slate-50 border-slate-200 text-slate-500
              dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  Showing{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {payments.length}
                  </span>{" "}
                  of{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {totalElements}
                  </span>{" "}
                  records.
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400">
                    Rows per page:
                  </span>
                  <select
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    disabled={isLoading}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold border appearance-none focus:outline-none focus:border-emerald-500/50
                      bg-white border-slate-200 text-slate-800
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200
                      disabled:opacity-50"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={page === 0 || isLoading}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1 || isLoading}
                    onClick={() => setPage((prev) => prev + 1)}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ───────────── ACTION MENU ───────────── */}
        {activeMenuId && menuAnchor && activePayment && (
          <div
            className="action-menu fixed z-[3000] w-48 rounded-xl border shadow-2xl overflow-hidden animate-fadeIn
              bg-white border-slate-200
              dark:bg-slate-900 dark:border-white/10"
            style={{
              top: menuAnchor.top,
              left: menuAnchor.left,
            }}
          >
            <button
              type="button"
              onClick={() => {
                openActionModal("view", activePayment);
                closeMenu();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-colors
                text-slate-600 hover:bg-slate-50 hover:text-slate-900
                dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
            >
              <FaEye className="text-[12px] opacity-70" />
              View Details
            </button>

            {activePayment?.irn && (
              <button
                type="button"
                onClick={() => {
                  setReceiptPaymentId(activePayment?.id);
                  setOpenReceipt(true);
                  closeMenu();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-colors
                  text-slate-600 hover:bg-slate-50 hover:text-slate-900
                  dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
              >
                <FaReceipt className="text-[12px] opacity-70" />
                Print Sales Receipt
              </button>
            )}

            {activePayment.status === "DRAFT" && (
              <button
                type="button"
                onClick={() => {
                  handleApprovePayment(activePayment.id);
                  closeMenu();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-colors
                  text-emerald-600 hover:bg-emerald-50
                  dark:text-emerald-400 dark:hover:bg-emerald-500/10"
              >
                <FaCheck className="text-[12px]" />
                Approve
              </button>
            )}

            {activePayment.status === "DRAFT" && (
              <>
                {confirmDeleteId === activePayment.id ? (
                  <div
                    className="border-t px-3 py-2.5
                    border-slate-100 bg-rose-50
                    dark:border-white/5 dark:bg-rose-500/5"
                  >
                    <p className="text-[10px] text-rose-600 dark:text-rose-400 font-medium mb-2 text-center">
                      Confirm permanent delete?
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          handleDeletePayment(activePayment.id);
                          closeMenu();
                        }}
                        className="flex-1 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-semibold transition"
                      >
                        Make Void
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition
                          bg-slate-100 hover:bg-slate-200 text-slate-600
                          dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(activePayment.id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-colors border-t
                      text-rose-600 hover:bg-rose-50 border-slate-100
                      dark:text-rose-400 dark:hover:bg-rose-500/10 dark:border-white/5"
                  >
                    <FaTrash className="text-[12px]" />
                    Make Void
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Unified Context-Driven Slide Up Drawer */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-900/40 dark:bg-slate-950/60 backdrop-blur-xs"
            onClick={() => setIsModalOpen(false)}
          />
          <div
            className={`relative w-full max-w-2xl mx-auto border rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto space-y-6 transition-transform duration-300 transform
              bg-white border-slate-200 shadow-[0_-15px_40px_rgba(0,0,0,0.15)]
              dark:bg-slate-900 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)]
              ${isModalOpen ? "translate-y-0" : "translate-y-full"}`}
          >
            <div className="flex justify-between items-start pb-3 border-b border-slate-200 dark:border-white/5">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
                <FaCoins className="text-emerald-500" /> Journal Entry
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl border transition
                  bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200
                  dark:bg-white/5 dark:border-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSavePayment} className="space-y-5">
              {error && (
                <div
                  className="p-3 rounded-xl text-xs font-medium border
                  bg-red-50 border-red-200 text-red-700
                  dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400"
                >
                  {error}
                </div>
              )}

              {/* VIEW MODE */}
              {modalMode === "view" && selectedPayment && (
                <div
                  className="rounded-xl p-5 border space-y-4 text-xs
                  bg-slate-50 border-slate-200
                  dark:bg-slate-950/40 dark:border-white/5"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Reference
                      </p>
                      <p className="font-mono text-slate-900 dark:text-white text-sm">
                        {selectedPayment.reference}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Description
                      </p>
                      <p className="font-mono text-slate-900 dark:text-white text-sm">
                        {selectedPayment.description}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Amount
                      </p>
                      <p className="font-mono text-slate-900 dark:text-white text-sm">
                        {Number(selectedPayment.amount).toFixed(2)}
                      </p>
                    </div>

                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Settlement Amount
                      </p>
                      <p className="font-mono text-slate-900 dark:text-white text-sm">
                        {Number(selectedPayment.settlementAmount).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Flow DIR
                      </p>
                      <p className="font-medium text-slate-700 dark:text-slate-300">
                        {selectedPayment.direction}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Payment Method
                      </p>
                      <p className="font-mono text-slate-700 dark:text-slate-300">
                        {selectedPayment.method}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Type
                      </p>
                      <p className="text-slate-700 dark:text-slate-300">
                        {selectedPayment.type}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Bank Commission
                      </p>
                      <p className="font-mono text-slate-900 dark:text-white text-sm">
                        {Number(selectedPayment.bankCommission).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-500 font-bold uppercase text-[10px]">
                        Date
                      </p>
                      <p className="font-mono text-slate-700 dark:text-slate-300">
                        {new Date(selectedPayment.paymentDate).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* MISC REVENUE */}
              {modalMode === "misc-revenue" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Type
                      </label>
                      <select
                        required
                        value={miscRevenueForm.typeStr}
                        onChange={(e) =>
                          setMiscRevenueForm({
                            ...miscRevenueForm,
                            typeStr: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
                          bg-slate-50 border-slate-200 text-slate-800
                          dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">---</option>

                        <option value="INVESTMENT">Owner Investment</option>
                        <option value="OWNER_DRAWING">Owner Drawing</option>
                        <option value="LOAN_RECEIVED">Loan Received</option>
                        <option value="LOAN_REPAYMENT">Loan Repayment</option>
                        <option value="LOAN_INTEREST">Loan Interest</option>
                        <option value="WITHHOLDING_TAX_PAYMENT">
                          Withholding Tax (Payable)
                        </option>
                        <option value="VAT_WITHHOLDING_PAYMENT">
                          VAT Withholding (Payable)
                        </option>
                        <option value="PAYE_PAYMENT">
                          Employee Income Tax (PAYE)
                        </option>
                        <option value="PENSION_PAYMENT">Pension</option>
                        <option value="EMPLOYEE_DEDUCTION">
                          Employee Deductions (Payable)
                        </option>
                        <option value="TAX_SETTLEMENT">
                          TAX Settlement (Output - Input - VWHR)
                        </option>
                        <option value="PROFIT_TAX_PAYMENT">
                          Profit Tax (- WHT Receivable)
                        </option>
                        <option value="REFUND_RECEIVABLE">
                          Refund From Government
                        </option>
                        <option value="OTHER_REVENUE">Other Revenue</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Amount
                      </label>
                      <input
                        required={!isAutoSettlement}
                        disabled={isAutoSettlement}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={
                          isAutoSettlement ? "Calculated automatically" : "0.00"
                        }
                        value={isAutoSettlement ? "" : miscRevenueForm.amount}
                        onChange={(e) =>
                          setMiscRevenueForm({
                            ...miscRevenueForm,
                            amount: parseFloat(e.target.value) || 0,
                          })
                        }
                        className={`w-full border rounded-xl px-4 py-2.5 text-xs font-mono text-right transition focus:outline-none focus:border-emerald-500/50 ${
                          isAutoSettlement
                            ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800/50 dark:border-slate-700 dark:text-slate-500"
                            : "bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                        }`}
                      />
                      {isAutoSettlement && (
                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                          TAX settlement amount is calculated automatically from
                          the ledger.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Payment Method
                      </label>
                      <select
                        required
                        value={miscRevenueForm.methodStr}
                        onChange={(e) =>
                          setMiscRevenueForm({
                            ...miscRevenueForm,
                            methodStr: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
                          bg-slate-50 border-slate-200 text-slate-800
                          dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">---</option>
                        <option value="CASH">CASH</option>
                        <option value="BANK">BANK</option>
                        <option value="MOBILE">MOBILE</option>
                        <option value="CHEQUE">CHEQUE</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Reference
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="e.g. EXT-REV-902"
                        value={miscRevenueForm.reference}
                        onChange={(e) =>
                          setMiscRevenueForm({
                            ...miscRevenueForm,
                            reference: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border font-mono transition focus:outline-none focus:border-emerald-500/50
                          bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
                          dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                      Description
                    </label>
                    <textarea
                      required
                      rows="2"
                      placeholder="Describe operational allocation notes..."
                      value={miscRevenueForm.description}
                      onChange={(e) =>
                        setMiscRevenueForm({
                          ...miscRevenueForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-xl px-4 py-2.5 text-xs border resize-none transition focus:outline-none focus:border-emerald-500/50
                        bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
                        dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                      Date
                    </label>
                    <input
                      required
                      type="date"
                      value={miscRevenueForm.date}
                      onChange={(e) =>
                        setMiscRevenueForm({
                          ...miscRevenueForm,
                          date: e.target.value,
                        })
                      }
                      className="w-full rounded-xl px-4 py-2.5 text-xs border transition focus:outline-none focus:border-emerald-500/50
      bg-slate-50 border-slate-200 text-slate-800
      dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                </div>
              )}

              {/* INTERNAL TRANSFER */}
              {modalMode === "transfer" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        From Account
                      </label>
                      <select
                        required
                        value={internalTransferForm.fromAccount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            fromAccount: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">-- Source Account --</option>
                        {actualAccounts.map((acc) => (
                          <option key={`from-${acc.id}`} value={acc.name}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        To Account
                      </label>
                      <select
                        required
                        value={internalTransferForm.toAccount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            toAccount: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">-- Destination Account --</option>
                        {actualAccounts.map((acc) => (
                          <option key={`to-${acc.id}`} value={acc.name}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Amount
                      </label>
                      <input
                        required
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={internalTransferForm.amount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            amount: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border text-right font-mono transition focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
            dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Reference
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="e.g. TRF-VOLT-01"
                        value={internalTransferForm.reference}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            reference: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border font-mono transition focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
            dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Payment Date
                      </label>
                      <input
                        required
                        type="date"
                        value={internalTransferForm.date}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            date: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* ✅ NEW Description Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                      Description
                    </label>
                    <textarea
                      required
                      rows="2"
                      placeholder="Describe the purpose of this transfer..."
                      value={internalTransferForm.description}
                      onChange={(e) =>
                        setInternalTransferForm({
                          ...internalTransferForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-xl px-4 py-2.5 text-xs border resize-none transition focus:outline-none focus:border-emerald-500/50
          bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
          dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                    />
                  </div>
                </div>
              )}

              {/* CORRECTION VOUCHER */}
              {modalMode === "correction" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Credit Account (−) */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Credit Account (−)
                      </label>
                      <select
                        required
                        value={internalTransferForm.fromAccount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            fromAccount: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">-- Credit Account --</option>
                        {journalAccounts.map((acc) => (
                          <option key={`from-${acc.id}`} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Debit Account (+) */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Debit Account (+)
                      </label>
                      <select
                        required
                        value={internalTransferForm.toAccount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            toAccount: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition appearance-none focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      >
                        <option value="">-- Debit Account --</option>
                        {journalAccounts.map((acc) => (
                          <option key={`to-${acc.id}`} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Amount
                      </label>
                      <input
                        required
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={internalTransferForm.amount}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            amount: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border text-right font-mono transition focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
            dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                      />
                    </div>

                    {/* ✅ NEW Date Field */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Date
                      </label>
                      <input
                        required
                        type="date"
                        value={internalTransferForm.date || ""}
                        onChange={(e) =>
                          setInternalTransferForm({
                            ...internalTransferForm,
                            date: e.target.value,
                          })
                        }
                        className="w-full rounded-xl px-4 py-2.5 text-xs border transition focus:outline-none focus:border-emerald-500/50
            bg-slate-50 border-slate-200 text-slate-800
            dark:bg-slate-950 dark:border-white/10 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* ✅ NEW Description Field */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400">
                      Description
                    </label>
                    <textarea
                      required
                      rows="2"
                      placeholder="e.g. Correction of excess 13.80 Birr – Suspense clearing"
                      value={internalTransferForm.description}
                      onChange={(e) =>
                        setInternalTransferForm({
                          ...internalTransferForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-xl px-4 py-2.5 text-xs border resize-none transition focus:outline-none focus:border-emerald-500/50
          bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400
          dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                    />
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold transition
                    bg-slate-100 hover:bg-slate-200 text-slate-700
                    dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  {modalMode === "view" ? "Close" : "Cancel"}
                </button>
                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition-all"
                  >
                    Submit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Sales Receipt Modal */}
        <PaymentReceipt
          isOpen={openReceipt}
          onClose={() => {
            setOpenReceipt(false);
            setReceiptPaymentId(null);
          }}
          paymentId={receiptPaymentId}
        />
      </div>
    </div>
  );
};

export default PaymentManagement;
